import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Collapse,
  Divider,
  Chip,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import UpgradePrompt from '../common/UpgradePrompt';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { useAuth } from '../../AuthContext';
import { proDesignTokens } from '../common/ProDesignTokens';
import { color, r } from '../../theme/tokens';
import { ScienceIcon, LockOutlinedIcon } from '../../icons';
import { useNavigate } from 'react-router-dom';
import { logAnalyticsEvent } from '../../utils/analytics';

// ────────────────────────────────────────────────────────────────
// Helper: safely parse JSONB fields that may arrive as strings
// ────────────────────────────────────────────────────────────────

function parseJsonField(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return null; }
}

// ────────────────────────────────────────────────────────────────
// Shared styles
// ────────────────────────────────────────────────────────────────

const sectionLabel = {
  fontSize: '0.7rem',
  fontWeight: 600,
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  mb: 0.5,
};

const bodyText = {
  fontSize: '0.84rem',
  color: 'text.primary',
  lineHeight: 1.65,
};

const secondaryText = {
  fontSize: '0.8rem',
  color: 'text.secondary',
  lineHeight: 1.55,
};

// ────────────────────────────────────────────────────────────────
// Helper: format dosing as readable text
// ────────────────────────────────────────────────────────────────

function formatDosing(dosing_protocol) {
  if (!dosing_protocol?.interventions?.length) return null;

  return dosing_protocol.interventions.map((d, i) => {
    const parts = [];
    if (d.substance) parts.push(d.substance);
    if (d.amount) parts.push(`${d.amount}${d.unit ? ' ' + d.unit : ''}`);
    if (d.frequency) parts.push(d.frequency);
    if (d.form) parts.push(d.form);
    if (d.timing) parts.push(d.timing);
    if (d.duration_weeks) parts.push(`${d.duration_weeks} weeks`);

    return { key: i, line: parts.join(' · ') };
  });
}

// ────────────────────────────────────────────────────────────────
// Helper: format participants as readable text
// ────────────────────────────────────────────────────────────────

function formatParticipants(pop) {
  if (!pop) return null;
  const parts = [];
  if (pop.n_total) parts.push(`${pop.n_total} participants`);
  if (pop.age_range) parts.push(`ages ${pop.age_range}`);
  else if (pop.age_mean) parts.push(`mean age ${pop.age_mean}`);
  if (pop.pct_female != null) parts.push(`${pop.pct_female}% female`);

  const line1 = parts.length > 0 ? parts.join(' · ') : null;
  const line2 = pop.health_status || null;

  // Don't return an object if there's nothing to show
  if (!line1 && !line2) return null;

  return { line1, line2 };
}

// ────────────────────────────────────────────────────────────────
// Helper: format safety as readable text
// ────────────────────────────────────────────────────────────────

function formatSafety(safety) {
  if (!safety) return null;

  const parts = [];
  if (safety.safety_conclusion) {
    parts.push(safety.safety_conclusion);
  } else {
    const aeCount = safety.adverse_events?.length || 0;
    const sae = safety.serious_adverse_events;
    if (aeCount > 0) {
      const descriptions = safety.adverse_events
        .slice(0, 3)
        .map(ae => {
          let s = ae.event;
          if (ae.pct_intervention != null) s += ` (${ae.pct_intervention}%)`;
          if (ae.severity) s += ` — ${ae.severity}`;
          return s;
        });
      parts.push(descriptions.join('. ') + '.');
    }
    if (sae === 0 || sae === '0') {
      parts.push('No serious adverse events.');
    }
  }

  if (safety.contraindications_mentioned?.length > 0) {
    parts.push('Contraindications: ' + safety.contraindications_mentioned.join(', ') + '.');
  }

  return parts.length > 0 ? parts.join(' ') : null;
}

// ────────────────────────────────────────────────────────────────
// Helper: format limitations as one-liner
// ────────────────────────────────────────────────────────────────

function formatLimitations(limitations) {
  if (!limitations?.length) return null;
  // Join first 2-3 limitations into a concise line
  return limitations.slice(0, 3).join('. ') + '.';
}

// ────────────────────────────────────────────────────────────────
// Study Details (expandable technical section)
// ────────────────────────────────────────────────────────────────

function StudyDetails({ pdfInsights }) {
  const [open, setOpen] = useState(false);
  const { key_findings } = pdfInsights;
  const methodology_quality = parseJsonField(pdfInsights.methodology_quality);
  const statistical_results = parseJsonField(pdfInsights.statistical_results);
  const population_details = parseJsonField(pdfInsights.population_details);
  const funding_and_conflicts = parseJsonField(pdfInsights.funding_and_conflicts);

  const hasContent =
    key_findings?.length > 0 ||
    methodology_quality?.study_design ||
    statistical_results?.primary_outcome?.measure ||
    funding_and_conflicts?.funding_source;

  if (!hasContent) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          py: 0.5,
          '&:hover': { opacity: 0.7 },
        }}
      >
        <Typography
          sx={{
            fontSize: '0.76rem',
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          Study details
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: '1rem',
            color: 'text.secondary',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        />
      </Box>

      <Collapse in={open}>
        <Box
          sx={{
            pt: 1,
            pl: 1.5,
            borderLeft: `${r(2)} solid`,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {/* Key Findings — full technical detail */}
          {key_findings?.length > 0 && (
            <Box>
              <Typography sx={sectionLabel}>Key Findings</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {key_findings.map((f, i) => (
                  <Box component="li" key={i} sx={secondaryText}>{f}</Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Methodology */}
          {methodology_quality?.study_design && (
            <Box>
              <Typography sx={sectionLabel}>Methodology</Typography>
              <Typography sx={secondaryText}>
                {[
                  methodology_quality.study_design,
                  methodology_quality.randomization_method && `Randomization: ${methodology_quality.randomization_method}`,
                  methodology_quality.blinding,
                  methodology_quality.intention_to_treat && `Intention-to-treat: ${methodology_quality.intention_to_treat}`,
                  methodology_quality.power_calculation && `Power: ${methodology_quality.power_calculation}`,
                  methodology_quality.attrition_rate && `Attrition: ${methodology_quality.attrition_rate}`,
                ].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
          )}

          {/* Statistical Results */}
          {statistical_results?.primary_outcome?.measure && (
            <Box>
              <Typography sx={sectionLabel}>Statistical Results</Typography>
              <Typography sx={secondaryText}>
                Primary: {statistical_results.primary_outcome.measure}
                {statistical_results.primary_outcome.between_group_difference &&
                  ` — ${statistical_results.primary_outcome.between_group_difference}`}
                {statistical_results.primary_outcome.p_value != null && (
                  <Typography variant="data">
                    {` (p = ${statistical_results.primary_outcome.p_value})`}
                  </Typography>
                )}
                {statistical_results.primary_outcome.effect_size != null &&
                  `, effect size: ${statistical_results.primary_outcome.effect_size}`}
                {statistical_results.primary_outcome.effect_size_type &&
                  ` (${statistical_results.primary_outcome.effect_size_type})`}
              </Typography>
              {statistical_results.secondary_outcomes?.length > 0 && (
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                  {statistical_results.secondary_outcomes.map((o, i) => (
                    <Box component="li" key={i} sx={secondaryText}>
                      {o.measure || 'Secondary'}: {o.between_group_difference || ''}
                      {o.p_value != null && (
                        <Typography variant="data">{` (p = ${o.p_value})`}</Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Population detail — inclusion/exclusion criteria */}
          {(population_details?.inclusion_criteria?.length > 0 ||
            population_details?.exclusion_criteria?.length > 0) && (
            <Box>
              <Typography sx={sectionLabel}>Eligibility Criteria</Typography>
              {population_details.inclusion_criteria?.length > 0 && (
                <Typography sx={secondaryText}>
                  Inclusion: {population_details.inclusion_criteria.join('; ')}
                </Typography>
              )}
              {population_details.exclusion_criteria?.length > 0 && (
                <Typography sx={{ ...secondaryText, mt: 0.25 }}>
                  Exclusion: {population_details.exclusion_criteria.join('; ')}
                </Typography>
              )}
            </Box>
          )}

          {/* Funding */}
          {(funding_and_conflicts?.funding_source || funding_and_conflicts?.conflicts_of_interest) && (
            <Box>
              <Typography sx={sectionLabel}>Funding & Conflicts</Typography>
              <Typography sx={secondaryText}>
                {[
                  funding_and_conflicts.funding_source,
                  funding_and_conflicts.industry_funded != null &&
                    (funding_and_conflicts.industry_funded ? 'Industry funded' : 'Not industry funded'),
                  funding_and_conflicts.conflicts_of_interest,
                ].filter(Boolean).join('. ')}
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ────────────────────────────────────────────────────────────────
// Main content for members
// ────────────────────────────────────────────────────────────────

function DeepAnalysisContent({ pdfInsights }) {
  const enhanced_summary = pdfInsights.plain_summary || pdfInsights.enhanced_summary;
  const clinical_insights = pdfInsights.clinical_insights;
  const limitations = pdfInsights.limitations;

  // JSONB fields may arrive as strings from the edge function — parse them
  const dosing_protocol = parseJsonField(pdfInsights.dosing_protocol);
  const population_details = parseJsonField(pdfInsights.population_details);
  const safety_profile = parseJsonField(pdfInsights.safety_profile);

  const dosingLines = formatDosing(dosing_protocol);
  const participants = formatParticipants(population_details);
  const safetyText = formatSafety(safety_profile);
  const limitationsText = formatLimitations(limitations);
  const controlText = dosing_protocol?.control;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

      {/* ── Opening paragraph ── */}
      {enhanced_summary && (
        <Typography sx={bodyText}>
          {enhanced_summary}
        </Typography>
      )}

      {/* ── Dosing / Participants / Safety — compact factual sections ── */}
      {(dosingLines || participants || safetyText) && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(12rem, 1fr))' },
            gap: 1.5,
            py: 1.25,
            px: 1.5,
            backgroundColor: 'grey.50',
            borderRadius: '0.375rem',
            border: `${r(1)} solid`,
            borderColor: 'divider',
          }}
        >
          {dosingLines && (
            <Box>
              <Typography sx={sectionLabel}>Dosing</Typography>
              {dosingLines.map(d => (
                <Typography key={d.key} sx={bodyText}>{d.line}</Typography>
              ))}
              {controlText && (
                <Typography sx={{ ...secondaryText, mt: 0.25 }}>
                  Control: {controlText}
                </Typography>
              )}
            </Box>
          )}

          {participants && (
            <Box>
              <Typography sx={sectionLabel}>Participants</Typography>
              {participants.line1 && (
                <Typography sx={bodyText}>{participants.line1}</Typography>
              )}
              {participants.line2 && (
                <Typography sx={secondaryText}>{participants.line2}</Typography>
              )}
            </Box>
          )}

          {safetyText && (
            <Box>
              <Typography sx={sectionLabel}>Safety</Typography>
              <Typography sx={bodyText}>{safetyText}</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* ── Clinical insights — actionable takeaways ── */}
      {clinical_insights?.length > 0 && (
        <Box>
          <Typography sx={sectionLabel}>Clinical Insights</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {clinical_insights.map((insight, i) => (
              <Box component="li" key={i} sx={bodyText}>{insight}</Box>
            ))}
          </Box>
        </Box>
      )}

      {/* ── Limitations — factual one-liner ── */}
      {limitationsText && (
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
          <WarningAmberIcon sx={{ fontSize: '0.9rem', color: 'text.secondary', mt: '0.15rem', flexShrink: 0 }} />
          <Typography sx={secondaryText}>
            {limitationsText}
          </Typography>
        </Box>
      )}

      {/* ── Study details — expandable technical section ── */}
      <StudyDetails pdfInsights={pdfInsights} />
    </Box>
  );
}

// ────────────────────────────────────────────────────────────────
// Locked preview for non-subscribers (no pdf_insights content)
// ────────────────────────────────────────────────────────────────

const LOCKED_SECTIONS = [
  'Enhanced Summary',
  'Clinical Dosing',
  'Participants',
  'Safety & Contraindications',
  'Outcomes',
  'Funded by',
];

function SkeletonRow() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', mb: '0.25rem' }}>
      <Box sx={{ height: '0.625rem', borderRadius: '0.25rem', backgroundColor: 'rgba(0,0,0,0.07)', width: '92%' }} />
      <Box sx={{ height: '0.625rem', borderRadius: '0.25rem', backgroundColor: 'rgba(0,0,0,0.05)', width: '78%' }} />
      <Box sx={{ height: '0.625rem', borderRadius: '0.25rem', backgroundColor: 'rgba(0,0,0,0.04)', width: '85%' }} />
    </Box>
  );
}

function DeepAnalysisLocked() {
  return (
    <Box>
      {/* All sections visible — headings readable, body rows blurred */}
      <Box sx={{ userSelect: 'none', pointerEvents: 'none', mb: '1rem' }}>
        {LOCKED_SECTIONS.map((label) => (
          <Box key={label} sx={{ mb: '1.125rem' }}>
            <Typography sx={{
              fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.08em', color: 'primary.main', mb: '0.3rem',
            }}>
              {label}
            </Typography>
            <Box sx={{ height: '0.0625rem', backgroundColor: 'divider', mb: '0.5rem' }} />
            <Box sx={{ filter: 'blur(0.25rem)', opacity: 0.45 }}>
              <SkeletonRow />
            </Box>
          </Box>
        ))}
      </Box>

      {/* Mini CTA — compact lock strip anchored below all sections */}
      <MiniUpgradeCta />
    </Box>
  );
}

function MiniUpgradeCta() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const handleClick = () => {
    logAnalyticsEvent('upgrade_prompt_clicked', { feature: 'deep_dive', source: 'deep_dive_locked' });
    navigate(currentUser ? '/subscribe' : '/login', {
      state: { from: { pathname: window.location.pathname }, source: 'deep_dive_locked' },
    });
  };

  useEffect(() => {
    logAnalyticsEvent('upgrade_prompt_shown', { feature: 'deep_dive', source: 'deep_dive_locked' });
  }, []);

  return (
    <Box
      sx={{
        mt: '0.5rem',
        pt: '1rem',
        borderTop: `0.0625rem solid rgba(46,125,50,0.15)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.75rem',
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <LockOutlinedIcon sx={{ fontSize: '0.9rem', color: proDesignTokens.colors.neutral[400], flexShrink: 0 }} />
        <Box>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: proDesignTokens.colors.neutral[700], lineHeight: 1.3 }}>
            Membership only
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: proDesignTokens.colors.neutral[500], mt: '0.125rem' }}>
            14-day free trial · Cancel anytime
          </Typography>
        </Box>
      </Box>
      <Button
        onClick={handleClick}
        variant="contained"
        size="small"
        endIcon={<ArrowForwardIcon sx={{ fontSize: '0.9rem' }} />}
        sx={{
          backgroundColor: proDesignTokens.colors.pro.accent,
          // Dark ink on the accent fill. `pro.accent` moved from the MUI green
          // to the signal accent in batch 7, which took near-white here from
          // 4.99:1 to 3.44:1; `accentInk` restores AA at 4.71:1 and matches the
          // correction PR 280 made everywhere else on an accent fill.
          color: color.accentInk,
          fontWeight: 600,
          fontSize: '0.8rem',
          textTransform: 'none',
          borderRadius: '0.5rem',
          px: '0.875rem',
          py: '0.5rem',
          flexShrink: 0,
          boxShadow: 'none',
          // Hover lightens to `accentHover` rather than darkening to evergreen,
          // which is the pattern `proDesignTokens.components.primaryButton`
          // already documents. Keeping the evergreen hover would put the dark
          // ink above at 1.74:1 — the resting fix has to carry the hover.
          '&:hover': { backgroundColor: color.accentHover, boxShadow: 'none' },
        }}
      >
        Start Free Trial
      </Button>
    </Box>
  );
}

// ────────────────────────────────────────────────────────────────
// Main section wrapper
// ────────────────────────────────────────────────────────────────

export default function DeepAnalysisSection({ article }) {
  const { hasAccess } = useFeatureAccess();
  const hasMemberAccess = hasAccess('pdf_insights');

  if (!article.has_pdf_insights) return null;

  const pdfInsights = article.pdf_insights;

  // pdf_insights is null for non-subscribers (edge function gates content
  // to hasSubscription === true). Show the locked layout — real section
  // headings + blurred skeleton + foreground upgrade CTA. No pdf_insights
  // bytes are in the DOM for non-subscribers.
  if (!pdfInsights) {
    return (
      <Box sx={{ mb: { xs: 3, sm: 4 }, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <ScienceIcon sx={{ fontSize: '1.1rem', color: proDesignTokens.colors.pro.accent }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.05rem' } }}>
            Detailed Insights
          </Typography>
          <LockOutlinedIcon sx={{ fontSize: '0.9rem', color: proDesignTokens.colors.neutral[400], ml: 'auto' }} />
        </Box>
        <Box
          sx={{
            borderRadius: proDesignTokens.borderRadius.lg,
            border: `${r(1)} solid ${proDesignTokens.colors.pro.accentMedium}`,
            backgroundColor: color.paper,
            overflow: 'hidden',
            p: { xs: 1.5, sm: 2 },
          }}
        >
          <DeepAnalysisLocked />
        </Box>
      </Box>
    );
  }

  // Don't render if there's no meaningful content in any structured field
  const _dp = parseJsonField(pdfInsights.dosing_protocol);
  const _pd = parseJsonField(pdfInsights.population_details);
  const _sp = parseJsonField(pdfInsights.safety_profile);
  const _sr = parseJsonField(pdfInsights.statistical_results);
  if (
    !pdfInsights.plain_summary &&
    !pdfInsights.enhanced_summary &&
    !pdfInsights.key_findings?.length &&
    !pdfInsights.clinical_insights?.length &&
    !_dp?.interventions?.length &&
    !_pd &&
    !_sp &&
    !_sr
  ) {
    return null;
  }

  return (
    <Box sx={{ mb: { xs: 3, sm: 4 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <ScienceIcon sx={{ fontSize: '1.1rem', color: proDesignTokens.colors.pro.accent }} />
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.05rem' } }}
        >
          Detailed Insights
        </Typography>
      </Box>

      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderRadius: proDesignTokens.borderRadius.lg,
          border: hasMemberAccess
            ? `${r(1)} solid ${proDesignTokens.colors.border.subtle}`
            : `${r(1)} solid ${proDesignTokens.colors.pro.accentMedium}`,
          backgroundColor: color.paper,
          ...(!hasMemberAccess && {
            boxShadow: `0 0 0 ${r(1)} ${proDesignTokens.colors.pro.accentLight}`,
          }),
        }}
      >
        {hasMemberAccess ? (
          <DeepAnalysisContent pdfInsights={pdfInsights} />
        ) : (
          // Use the locked layout (no pdf content in DOM) even when pdfInsights
          // is non-null — this handles stale Firebase claim edge cases where the
          // edge function returned content but the client-side gate says no access.
          <DeepAnalysisLocked />
        )}
      </Box>
    </Box>
  );
}
